/**
 * 用户端 UI 语言配置 — 与 react-i18next code、localStorage、API Accept-Language 对齐。
 *
 * 新语言 UI 文案:复制 en.ts 为 locales/<code>.ts 并逐步翻译;
 * 未译 key 通过 i18next fallback 回 en → zh。
 */
export interface UiLangDef {
  /** react-i18next / localStorage `silkreels.lang` */
  code: string
  /** 语言切换器展示名(用目标语言自称) */
  label: string
  /** 传给 backend Accept-Language 的 BCP-47 tag */
  apiLocale: string
}

/** 与竞品站一致的语言列表 + 中文/英文 */
export const UI_LANGS = [
  { code: 'zh', label: '中文', apiLocale: 'zh-CN' },
  { code: 'en', label: 'English', apiLocale: 'en-US' },
  { code: 'fr', label: 'Français', apiLocale: 'fr-FR' },
  { code: 'de', label: 'Deutsch', apiLocale: 'de-DE' },
  { code: 'es', label: 'Español', apiLocale: 'es-ES' },
  { code: 'pt', label: 'Português', apiLocale: 'pt-BR' },
  { code: 'it', label: 'Italiano', apiLocale: 'it-IT' },
  { code: 'id', label: 'Indonesia', apiLocale: 'id-ID' },
  { code: 'ko', label: '한국어', apiLocale: 'ko-KR' },
  { code: 'ja', label: '日本語', apiLocale: 'ja-JP' },
  { code: 'ar', label: 'العربية', apiLocale: 'ar' },
] as const satisfies readonly UiLangDef[]

export type UiLangCode = (typeof UI_LANGS)[number]['code']

const byCode = new Map<string, UiLangDef>(UI_LANGS.map((l) => [l.code, l]))

/** UI code → API BCP-47(供 fetch Accept-Language) */
export function uiLangToApiLocale(code: string | null | undefined): string {
  if (!code) return 'zh-CN'
  const base = code.split('-')[0]?.toLowerCase() ?? code
  return byCode.get(base)?.apiLocale ?? byCode.get(code)?.apiLocale ?? 'en-US'
}

/** 浏览器 / i18next 语言 tag → 归一化 UI code */
export function normalizeUiLang(raw: string | null | undefined): UiLangCode {
  if (!raw) return 'zh'
  const lower = raw.toLowerCase().replace('_', '-')
  const base = lower.split('-')[0] ?? lower
  if (byCode.has(base)) return base as UiLangCode
  if (base === 'zh') return 'zh'
  if (base === 'en') return 'en'
  return 'en'
}

/** Date/Number 等 Intl 用的 BCP-47 */
export function uiLangToIntlLocale(code: string): string {
  return uiLangToApiLocale(code)
}

export const SUPPORTED_LANGS = UI_LANGS.map(({ code, label }) => ({ code, label }))
